import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useProfile } from '@/hooks/useUserQueries';
import { queryKeys } from '@/lib/queryKeys';
import { ensureSession } from '@/lib/ensureSession';

export interface ProgramTargets {
  calories?: number;
  protein?: number;
  carbs?: number;
  fats?: number;
  sessions_per_week?: number;
}

export interface ProgramMilestone {
  week: number;
  description: string;
  metric?: string;
  target?: string | number;
}

export interface CoachProgram {
  id: string;
  user_id: string;
  created_at: string;
  updated_at: string;
  goal: string;
  goal_target: Record<string, unknown>;
  start_date: string;
  end_date: string;
  duration_weeks: number;
  current_week: number;
  phase: string;
  weekly_split: Record<string, string>;
  targets: ProgramTargets;
  progression_rules: Record<string, number>;
  milestones: ProgramMilestone[];
  programme_name: string | null;
  coach_summary: string | null;
  start_weight_kg: number | null;
  status: 'active' | 'paused' | 'completed' | 'superseded';
  last_adjusted_at: string | null;
  adjustment_reason: string | null;
  completed_at: string | null;
}

export interface CoachProgramUpdate {
  id: string;
  program_id: string;
  user_id: string;
  created_at: string;
  change_type:
    | 'program_created'
    | 'auto_increase'
    | 'auto_decrease'
    | 'checkin_confirmed'
    | 'photo_adjustment';
  summary: string;
  details: Record<string, unknown>;
  acknowledged_at: string | null;
}

/**
 * Fetches the user's currently active coaching programme.
 *
 * Resilience strategy: we DO NOT rely solely on `profiles.active_program_id`,
 * because that pointer can be stale in the local profile cache (e.g. the
 * profile was cached before the program was created, or the network refresh
 * hasn't landed yet). Instead the primary lookup is the canonical source of
 * truth: `coach_programs WHERE status = 'active'`. The profile pointer is
 * only used as a secondary tiebreaker. This prevents the home screen
 * programme card from "disappearing" when the profile cache is behind.
 */
export function useActiveProgram() {
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const activeId = (profile as any)?.active_program_id as string | null | undefined;

  return useQuery({
    queryKey: [...queryKeys.activeProgram, user?.id],
    queryFn: async (): Promise<CoachProgram | null> => {
      if (!user) return null;

      // Primary + single source of truth: profiles.active_program_id. The
      // edge functions and the DB stamp trigger use the same pointer, so the
      // client must agree with them rather than run its own heuristic.
      if (activeId) {
        const { data, error } = await (supabase as any)
          .from('coach_programs')
          .select('*')
          .eq('id', activeId)
          .eq('user_id', user.id)
          .maybeSingle();
        if (error) {
          console.error('useActiveProgram pointer error:', error);
        } else if (data) {
          return data as CoachProgram;
        }
      }

      // Defensive fallback (pointer null/stale): latest active programme.
      const { data: activeRows, error: activeErr } = await (supabase as any)
        .from('coach_programs')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .order('created_at', { ascending: false })
        .limit(1);
      if (activeErr) {
        console.error('useActiveProgram active query error:', activeErr);
      }
      const activeRow = (activeRows as CoachProgram[] | null)?.[0] ?? null;
      if (activeRow) return activeRow;

      // Fallback: most recently completed programme — the card uses
      // completed_at + local timezone to decide whether to show the
      // "Programme Complete" celebration card (same calendar day only).
      const { data: recentRows } = await (supabase as any)
        .from('coach_programs')
        .select('*')
        .eq('user_id', user.id)
        .order('completed_at', { ascending: false, nullsFirst: false })
        .limit(1);
      const recentRow = (recentRows as CoachProgram[] | null)?.[0] ?? null;
      if (recentRow && recentRow.status === 'completed') return recentRow;

      return null;
    },
    enabled: !!user,
    staleTime: 5 * 60 * 1000,
    // Keep showing the previous program while a refetch is in flight so the
    // card never flashes empty between the cached profile and a fresh load.
    placeholderData: keepPreviousData,
  });
}

export function useProgramUpdates(unacknowledgedOnly = false) {
  const { user } = useAuth();
  return useQuery({
    queryKey: [...queryKeys.programUpdates, user?.id, unacknowledgedOnly],
    queryFn: async (): Promise<CoachProgramUpdate[]> => {
      if (!user) return [];
      let q = (supabase as any)
        .from('coach_program_updates')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      if (unacknowledgedOnly) q = q.is('acknowledged_at', null);
      const { data, error } = await q;
      if (error) {
        console.error('useProgramUpdates error:', error);
        return [];
      }
      return (data as CoachProgramUpdate[]) ?? [];
    },
    enabled: !!user,
    staleTime: 60 * 1000,
  });
}

export function useAcknowledgeProgramUpdate() {
  const { user } = useAuth();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (updateId: string) => {
      if (!user) throw new Error('Not authenticated');
      await ensureSession();
      const nowIso = new Date().toISOString();

      // Fetch the target row so we can clear every duplicate sibling in one
      // write. Without this, dismiss only acks one row of a duplicate set and
      // the banner reappears on the next refetch.
      const { data: target, error: fetchErr } = await (supabase as any)
        .from('coach_program_updates')
        .select('program_id, change_type, summary')
        .eq('id', updateId)
        .eq('user_id', user.id)
        .maybeSingle();
      if (fetchErr) throw fetchErr;

      if (target?.program_id && target?.change_type && target?.summary != null) {
        const { error } = await (supabase as any)
          .from('coach_program_updates')
          .update({ acknowledged_at: nowIso })
          .eq('user_id', user.id)
          .eq('program_id', target.program_id)
          .eq('change_type', target.change_type)
          .eq('summary', target.summary)
          .is('acknowledged_at', null);
        if (error) throw error;
      } else {
        // Fallback: ack the single row by id.
        const { error } = await (supabase as any)
          .from('coach_program_updates')
          .update({ acknowledged_at: nowIso })
          .eq('id', updateId)
          .eq('user_id', user.id);
        if (error) throw error;
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...queryKeys.programUpdates, user?.id] });
    },
  });
}

export interface GenerateProgramInput {
  goalDescription?: string;
  durationWeeks?: number;
  /** Carry the current split + exercises forward, re-tuned to the new goal. */
  keepExercises?: boolean;
}

export class ProgramPaywallError extends Error {
  code = 'PROGRAM_LIMIT_REACHED' as const;
  constructor(message: string) {
    super(message);
    this.name = 'ProgramPaywallError';
  }
}

export function useGenerateProgram() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: GenerateProgramInput = {}) => {
      await ensureSession();
      const body: Record<string, unknown> = {};
      if (input.goalDescription?.trim()) body.goal_description = input.goalDescription.trim();
      if (typeof input.durationWeeks === 'number') body.duration_weeks = input.durationWeeks;
      body.keep_exercises = input.keepExercises ?? false;
      const { data, error } = await supabase.functions.invoke('generate-coach-program', {
        body,
      });
      // Edge function returns a structured 402 body when the free-plan
      // program quota is exhausted. supabase-js surfaces non-2xx as `error`
      // but the JSON body still rides on `data`. Detect either shape.
      const payload = (data as any) ?? null;
      if (payload?.code === 'program_paywall' || payload?.error === 'PROGRAM_LIMIT_REACHED') {
        throw new ProgramPaywallError(payload?.message || 'Upgrade required');
      }
      if (error) {
        const msg = (error as any)?.message || '';
        if (/PROGRAM_LIMIT_REACHED|program_paywall|402/i.test(msg)) {
          throw new ProgramPaywallError('Upgrade required');
        }
        throw error;
      }
      return data;
    },
    onSuccess: (data) => {
      if ((data as any)?.superseded) {
        toast.success('Your previous programme has been completed. Starting your new plan.');
      }
      qc.invalidateQueries({ queryKey: queryKeys.profile });
      qc.invalidateQueries({ queryKey: [...queryKeys.activeProgram, user?.id] });
      qc.invalidateQueries({ queryKey: [...queryKeys.programUpdates, user?.id] });
      qc.invalidateQueries({ queryKey: queryKeys.routines });
      qc.invalidateQueries({ queryKey: [...queryKeys.chatMessages, user?.id] });
      // Health Score Engine: onboarding just supplied the profile fields
      // (sleep hours, training frequency, calorie target) that feed scoring.
      qc.invalidateQueries({ queryKey: ['healthScore', user?.id] });
      qc.invalidateQueries({ queryKey: ['dailyBrief', user?.id] });
    },
  });
}

/**
 * Rename the user's active programme. Empty/whitespace clears the name so the
 * UI falls back to the default "${duration_weeks}-Week Programme" label.
 */
export function useRenameProgram() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ programId, name }: { programId: string; name: string }) => {
      if (!user) throw new Error('Not authenticated');
      await ensureSession();
      const trimmed = name.trim().slice(0, 60);
      const value = trimmed.length === 0 ? null : trimmed;
      const { error } = await (supabase as any)
        .from('coach_programs')
        .update({ programme_name: value, updated_at: new Date().toISOString() })
        .eq('id', programId)
        .eq('user_id', user.id);
      if (error) throw error;
      return value;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...queryKeys.activeProgram, user?.id] });
      toast.success('Programme renamed');
    },
    onError: (err: any) => {
      console.error('useRenameProgram error:', err);
      toast.error(err?.message || 'Could not rename programme');
    },
  });
}

/**
 * Rewinds the user's active programme back to week 1 without touching the
 * plan (weekly_split / routines) or workout history. Server-side RPC bypasses
 * the protect_coach_program_fields trigger which normally blocks direct
 * client writes to current_week / start_date / end_date / last_advanced_at.
 */
export function useResetProgram() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (programId: string) => {
      if (!user) throw new Error('Not authenticated');
      await ensureSession();
      const { error } = await (supabase as any).rpc('reset_coach_program', {
        p_program_id: programId,
      });
      if (error) throw error;
    },
    onSuccess: async () => {
      // Force an immediate refetch of the programme row even though the Home
      // screen (the primary observer) is unmounted while this mutation runs
      // from EditProgrammeManual. Without this, keepPreviousData would render
      // the stale start_date on return to Home and the progress bar would
      // briefly show the old week before the background refetch lands.
      await qc.refetchQueries({ queryKey: [...queryKeys.activeProgram, user?.id] });
      // Programme-derived siblings: mark stale so they refresh on next mount.
      qc.invalidateQueries({ queryKey: [...queryKeys.programUpdates, user?.id] });
      qc.invalidateQueries({ queryKey: queryKeys.profile });
      qc.invalidateQueries({ queryKey: queryKeys.routines });
      qc.invalidateQueries({ queryKey: ['transformation-checkins', user?.id] });
      qc.invalidateQueries({ queryKey: ['profile-photos', user?.id] });
      qc.invalidateQueries({ queryKey: ['profile-analysis', user?.id] });
      qc.invalidateQueries({ queryKey: ['profile-checkins-completed', user?.id] });
      qc.invalidateQueries({ queryKey: ['profile-checkin-today', user?.id] });
      qc.invalidateQueries({ queryKey: ['weekly-checkin-status'] });
      qc.invalidateQueries({ queryKey: ['weekly-checkin-status-ext'] });
      qc.invalidateQueries({ queryKey: ['checkin-prompt-photos'] });
      toast.success('Programme reset to week 1');
    },
    onError: (err: any) => {
      console.error('useResetProgram error:', err);
      toast.error(err?.message || 'Could not reset programme');
    },
  });
}

